'use client';

import { forwardRef, useContext, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { ComboboxOptionContext } from './context/combobox-option.context';
import { ComboboxRootContext } from './context/combobox-root.context';
import { comboboxOptionMediaRecipe } from './styles/combobox-option-media.styles';
import type { ComboboxOptionMediaPropsInterface } from './types/combobox-option-media.types';

/**
 * @since 10.0.0
 */
export const ComboboxOptionMedia = forwardRef<HTMLSpanElement, ComboboxOptionMediaPropsInterface>(
  (props: ComboboxOptionMediaPropsInterface, forwardedRef) => {
    const { children, className, css: cssProp, ...rest } = props;
    const { combobox, showOptionControl } = useContext(ComboboxRootContext);
    const { disabled, selected } = useContext(ComboboxOptionContext);

    const appearance = useMemo(
      () =>
        combobox.isMultiSelect
          ? // If in multi-select mode, prioritize checkbox appearance, unless children (icon) is provided
            children
            ? undefined
            : 'checkbox'
          : // If in single-select mode, check if showOptionControl to display radio
            showOptionControl
            ? 'radio'
            : undefined,
      [combobox.isMultiSelect, children, showOptionControl],
    );

    const styles = css(comboboxOptionMediaRecipe.raw({ appearance, disabled, selected }), cssProp);

    return (
      <span
        ref={forwardedRef}
        data-c11n-component="Combobox.OptionMedia"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', styles, className)}
        {...rest}
      >
        {appearance ? null : children}
      </span>
    );
  },
);

ComboboxOptionMedia.displayName = 'Combobox.OptionMedia';
